import type { ReactElement } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { RelativeTimeWithTooltip } from '@/components/shared/RelativeTimeWithTooltip';
import { getUserDisplayName } from '@/lib/utils';
import { UserTypeTag } from './UserTypeTag';
import type { GroupedAlert } from '@kirin/api-client/contracts/alerts';

export interface AlertUsersDialogProps {
  alert: GroupedAlert;
  open: boolean;
  onClose: () => void;
}

/**
 * Dialog showing users affected by a grouped alert: name/email, last seen, event count.
 * Data comes from alert.users (no extra API).
 */
export function AlertUsersDialog({
  alert,
  open,
  onClose,
}: AlertUsersDialogProps): ReactElement {
  const { users } = alert;

  return (
    <Dialog open={open} onOpenChange={open => !open && onClose()}>
      <DialogContent className="max-w-lg" showCloseButton>
        <DialogHeader>
          <DialogTitle>Users with this alert</DialogTitle>
        </DialogHeader>
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead className="w-[120px]">Last Seen</TableHead>
                <TableHead className="w-[80px] text-right">Instances</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map(user => (
                <TableRow key={user.id}>
                  <TableCell className="text-sm">
                    <span className="inline-flex items-center gap-1.5">
                      {getUserDisplayName(user)}
                      <UserTypeTag
                        userType={user.userType}
                        named={!!user.name}
                        offboarded={user.offboarded}
                        guest={user.guest}
                      />
                    </span>
                  </TableCell>
                  <TableCell className="text-sm">
                    {user.lastSeenAt ? (
                      <RelativeTimeWithTooltip date={user.lastSeenAt} />
                    ) : (
                      '—'
                    )}
                  </TableCell>
                  <TableCell className="text-sm text-right">
                    {user.occurrenceCount ?? '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </DialogContent>
    </Dialog>
  );
}

AlertUsersDialog.displayName = 'AlertUsersDialog';
